import Tool, { BaseTool, type ToolButtonComponent } from './Tool';
import { ApplicationView, useController } from '@aha-app/mvc';
import React, { useRef } from 'react';
import ToolbarMenu from '../toolbars/ToolbarMenu';
import ToolbarRow from '../toolbars/ToolbarRow';
import ToolButton from '../toolbars/ToolButton';
import { svgToIcon } from '../utils/svg';
import WhiteboardController from '../controllers/WhiteboardController';
import { type Instance as TippyInstance } from 'tippy.js/index';

interface GenericButtonOptions {
  iconClass?: string;
  iconSVG?: string;
}

export interface GenericButtonProps {
  currentValue: unknown;
  setValue: (v: unknown) => void;
  hidePopup: () => void;
}
type IGenericButton = React.FC<GenericButtonProps>;

const GenericButton = (
  tool: typeof BaseTool,
  options: GenericButtonOptions & { value: unknown }
) => {
  const Button: IGenericButton = ApplicationView(
    ({ currentValue, setValue, hidePopup }) => (
      <ToolButton
        active={currentValue === options.value}
        onClick={() => {
          setValue(tool);
          hidePopup();
        }}
        tool={tool}
      >
        {options.iconClass && <aha-icon icon={options.iconClass} />}
        {options.iconSVG && svgToIcon(options.iconSVG)}
      </ToolButton>
    )
  );
  return Button;
};

const Popup: React.FC<{
  tool: typeof GenericListToolCls;
  selection: fabric.Object;
  attribute: string;
  hidePopup: () => void;
}> = ApplicationView(({ tool, selection, attribute, hidePopup }) => {
  const controller = useController(WhiteboardController);
  const listButtons = tool.toolChoices.map(choice => choice.buttonComponent());

  const setValue = (tool: typeof BaseTool) => {
    controller.actionApplyTool(tool);
  };

  const currentValue = selection.firstObject()[attribute];

  return (
    <ToolbarMenu columns={listButtons.length}>
      <ToolbarRow>
        {listButtons.map((ListButton, index) => (
          <ListButton
            currentValue={currentValue}
            key={index}
            setValue={setValue}
            hidePopup={hidePopup}
          />
        ))}
      </ToolbarRow>
    </ToolbarMenu>
  );
});

const genericListButton = (
  tool: typeof GenericListToolCls,
  options: GenericButtonOptions & { attribute: string }
) => {
  const Button: ToolButtonComponent = ApplicationView(({ selection }) => {
    const tippyRef = useRef<TippyInstance>();

    return (
      <ToolButton
        popup={
          <Popup
            selection={selection}
            attribute={options.attribute}
            tool={tool}
            hidePopup={() => tippyRef.current?.hide()}
          />
        }
        tippyRef={tippyRef}
        tool={tool}
      >
        {options.iconClass && <aha-icon icon={options.iconClass} />}
        {options.iconSVG && svgToIcon(options.iconSVG)}
      </ToolButton>
    );
  });
  return Button;
};

// these really act as interfaces, but interfaces can't define static properties, so they have to be a classes
class GenericTool extends BaseTool {
  static buttonComponent(): IGenericButton {
    throw new Error('Implement buttonComponent for GenericTool');
  }
}

class GenericListToolCls extends Tool {
  static toolName: string;
  static toolChoices: (typeof GenericTool)[];
}

const GenericListTool = (
  options: GenericButtonOptions & {
    toolName: string;
    attribute: string;
    choices: { toolName: string; value: unknown }[];
  }
): typeof GenericListToolCls => {
  return class extends GenericListToolCls {
    static toolName = options.toolName;

    static toolChoices = options.choices.map(
      choice =>
        class extends GenericTool {
          static toolName = choice.toolName;

          // Invoke the tool action.
          static action({ controller, selection }) {
            selection.setOnObjects(options.attribute, choice.value);
            setTimeout(() => controller.canvas.requestRenderAll());
          }

          static buttonComponent() {
            return GenericButton(this, choice);
          }
        }
    );

    // Render a component to render the toolbar button for the tool.
    static buttonComponent() {
      return genericListButton(this, options);
    }
  };
};

export default GenericListTool;
